/**
 * Итоги по кредиторам и контроль непротиворечивости сумм (ТЗ FR-27, правило R-04).
 * Все расчёты — в копейках целыми числами, см. `Kopecks`.
 */

import type { CaseProfile } from '../types/profile.js';
import type { Creditor, ObligatoryPayment } from '../types/creditor.js';
import type { Kopecks } from '../types/common.js';

export interface DebtTotals {
  /**
   * Общая сумма задолженности. Складывается из текущей задолженности
   * перед кредиторами и недоимки по обязательным платежам: налоги входят
   * в общую сумму долга (решение по вопросу 22, docs/decisions.md).
   * Вручную не редактируется.
   */
  total: Kopecks;
  /** Итог по обязательствам предпринимательского характера (раздел II формы) */
  businessTotal: Kopecks;
  /** Итог по прочим обязательствам (раздел I формы) */
  personalTotal: Kopecks;
  /** Отдельно — только денежные обязательства перед кредиторами */
  creditorsTotal: Kopecks;
  /** Отдельно — только недоимка по обязательным платежам */
  obligatoryTotal: Kopecks;
  creditorsCount: number;
  /** Есть хотя бы одно обязательство с просрочкой свыше трёх месяцев */
  hasOverdueOverThreeMonths: boolean;
}

/** Сумма текущей задолженности: именно она идёт в текст заявления. */
export function sumCreditors(creditors: Creditor[]): Kopecks {
  return creditors.reduce((acc, c) => acc + Math.round(c.amountDebt || 0), 0);
}

export function sumObligatoryPayments(payments: ObligatoryPayment[]): Kopecks {
  // Пени и штрафы входят в общую сумму (решение заказчика 26.10.2026)
  return payments.reduce((acc, p) => acc + Math.round(p.arrears || 0) + Math.round(p.penalties || 0), 0);
}

export function computeTotals(profile: CaseProfile): DebtTotals {
  const creditors = profile.creditors ?? [];
  const payments = profile.obligatoryPayments ?? [];

  const creditorsTotal = sumCreditors(creditors);
  const obligatoryTotal = sumObligatoryPayments(payments);

  const businessTotal =
    sumCreditors(creditors.filter((c) => c.isBusiness)) +
    sumObligatoryPayments(payments.filter((p) => p.isBusiness));
  const personalTotal =
    sumCreditors(creditors.filter((c) => !c.isBusiness)) +
    sumObligatoryPayments(payments.filter((p) => !p.isBusiness));

  return {
    total: creditorsTotal + obligatoryTotal,
    businessTotal,
    personalTotal,
    creditorsTotal,
    obligatoryTotal,
    creditorsCount: creditors.length + payments.length,
    hasOverdueOverThreeMonths: creditors.some((c) => c.overdueOverThreeMonths),
  };
}

/**
 * Проверка FR-27: сумма в тексте заявления и сумма в списке кредиторов
 * считаются из одного набора данных, поэтому расхождение возможно только
 * при повреждении данных. Проверка оставлена как защита от него.
 */
export function creditorsTotalsConsistent(profile: CaseProfile): boolean {
  const totals = computeTotals(profile);
  return totals.total === totals.businessTotal + totals.personalTotal;
}

/** Суммарная стоимость имущества по всем шести разделам описи. */
export function computePropertyValue(profile: CaseProfile): Kopecks {
  const p = profile.property;
  const sum = (arr: Array<{ cost?: Kopecks }>) => arr.reduce((a, x) => a + Math.round(x.cost || 0), 0);
  return (
    sum(p.realty) +
    sum(p.vehicles) +
    p.accounts.reduce((a, x) => a + Math.round(x.balance || 0), 0) +
    sum(p.shares) +
    p.securities.reduce((a, x) => a + Math.round(x.nominal || 0), 0) +
    sum(p.valuables)
  );
}
